// @ts-check

import React, { useState, useCallback, useEffect } from "react";
import {
  Popover,
  Button,
  DatePicker,
  FormLayout,
  Select,
  TextField,
  ButtonGroup,
} from "@shopify/polaris";
import { CalendarIcon } from "@shopify/polaris-icons";

import {
  format,
  endOfDay,
  startOfYesterday,
  endOfYesterday,
  startOfDay,
  subDays,
  startOfWeek,
  startOfMonth,
} from "date-fns";

import { ActivatorButton } from "components/DateRangePicker/ActivatorButton";
import { calculateCurrentDatePeriodType } from "components/DateRangePicker/util";

const TODAY = new Date();

export const DateRangePicker = ({
  dates,
  onChange,
}) => {
  const [popoverActive, setPopoverActive] = useState(false);

  const [currentDates, setCurrentDates] = useState({
    start: startOfDay(subDays(TODAY, 6)),
    end: endOfDay(TODAY),
  });
  const [currentDatePeriodType, setCurrentDatePeriodType] = useState("lastSevenDays");
  const [{ month, year }, setVisibleDatePeriod] = useState({
    month: Number(format(new Date(), "M")) - 1,
    year: Number(format(new Date(), "yyyy")),
  });

  useEffect(() => {
    if (dates) {
      setCurrentDates(dates);
    }
  }, [dates]);

  useEffect(() => {
    if (currentDates) {
      const currentDatePeriodType = calculateCurrentDatePeriodType(currentDates);
      setCurrentDatePeriodType(currentDatePeriodType);
    }
  }, [currentDates]);

  const handleDatePeriodChange = useCallback((month, year) => {
    setVisibleDatePeriod({ month, year });
  }, []);

  const handleDatePeriodSelect = useCallback((val) => {
    let currentDates = { start: new Date(), end: new Date() };

    switch (val) {
      case "today":
        currentDates.start = startOfDay(TODAY);
        currentDates.end = endOfDay(TODAY);
        break;
      case "yesterday":
        currentDates.start = startOfYesterday();
        currentDates.end = endOfYesterday();
        break;
      case "lastSevenDays":
        currentDates.start = startOfDay(subDays(TODAY, 6));
        currentDates.end = endOfDay(TODAY);
        break;
      case "lastFourteenDays":
        currentDates.start = startOfDay(subDays(TODAY, 13));
        currentDates.end = endOfDay(TODAY);
        break;
      case "lastThirtyDays":
        currentDates.start = startOfDay(subDays(TODAY, 29));
        currentDates.end = endOfDay(TODAY);
        break;
      case "lastNinetyDays":
        currentDates.start = startOfDay(subDays(TODAY, 89));
        currentDates.end = endOfDay(TODAY);
        break;
      case "weekToDate":
        currentDates.start = startOfWeek(TODAY);
        currentDates.end = endOfDay(TODAY);
        break;
      case "monthToDate":
        currentDates.start = startOfMonth(TODAY);
        currentDates.end = endOfDay(TODAY);
        break;
      default:
      // custom, do nothing
    }

    setCurrentDates(currentDates);
    setCurrentDatePeriodType(val);
  }, []);

  if (!dates) {
    return (
      <Button icon={CalendarIcon} loading>
        Select dates
      </Button>
    );
  }

  const handleActivatorClick = () => {
    if (popoverActive) {
      const startDate = dates.start;
      setVisibleDatePeriod({
        month: Number(format(startDate, "M")) - 1,
        year: Number(format(startDate, "yyyy")),
      });
    }

    setPopoverActive((prev) => !prev);
  };

  const handleSave = () => {
    setPopoverActive(false);

    const dateRange = {
      start: currentDates.start,
      end: endOfDay(currentDates.end),
    };

    onChange(dateRange);
  };

  const handleCancel = () => {
    setPopoverActive(false);
  };

  const activator = (
    <ActivatorButton
      dates={dates}
      handleClick={handleActivatorClick}
    />
  );

  return (
    <Popover
      active={popoverActive}
      activator={activator}
      ariaHaspopup={false}
      onClose={() => setPopoverActive(false)}
      sectioned
      fullHeight={true}
    >
      <FormLayout>
        <Select
          label="Date Range"
          value={currentDatePeriodType}
          options={[
            { label: "Today", value: "today" },
            { label: "Yesterday", value: "yesterday" },
            { label: "Last 7 Days", value: "lastSevenDays" },
            { label: "Last 14 Days", value: "lastFourteenDays" },
            { label: "Last 30 Days", value: "lastThirtyDays" },
            { label: "Last 90 Days", value: "lastNinetyDays" },
            { label: "Week To Date", value: "weekToDate" },
            { label: "Month To Date", value: "monthToDate" },
            { label: "Custom", value: "custom" },
          ]}
          onChange={handleDatePeriodSelect}
        />

        <FormLayout.Group condensed>
          <TextField
            label="Start Date"
            value={format(currentDates.start, "yyyy-MM-dd")}
            autoComplete="off"
            disabled
          />
          <TextField
            label="End Date"
            value={format(currentDates.end, "yyyy-MM-dd")}
            autoComplete="off"
            disabled
          />
        </FormLayout.Group>

        <DatePicker
          month={month}
          disableDatesAfter={TODAY}
          year={year}
          onChange={({ start, end }) => {
            setCurrentDates({ start, end })
          }}
          onMonthChange={handleDatePeriodChange}
          selected={{
            start: currentDates.start,
            end: currentDates.end,
          }}
          allowRange
        />

        <ButtonGroup fullWidth>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="primary">Save</Button>
        </ButtonGroup>
      </FormLayout>
    </Popover>
  );
};
